import React, { useState, useEffect } from "react";
import "./Payments.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import AxiosUrl from "../AxiosUrl";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Pagination from "@mui/material/Pagination";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import useMediaQuery from "@mui/material/useMediaQuery";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import moment from "moment";
import { DeletePaymentModal } from "./DeletePaymentModal";

function Payments() {
  const uid = localStorage.getItem("id");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [totalPayments, setTotalPayments] = useState(0);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Click-outside counters
  const [dateClicks, setDateClicks] = useState(0);
  const [typeClicks, setTypeClicks] = useState(0);
  const [statusClicks, setStatusClicks] = useState(0);
  const [platformClicks, setPlatformClicks] = useState(0);

  // Currently open menu
  const [openMenu, setOpenMenu] = useState(null);

  const isMobile = useMediaQuery("(max-width:880px)");
  const isSmallMobile = useMediaQuery("(max-width:480px)");
  
  // View mode: 'grid' or 'list' - default to list
  const [viewMode, setViewMode] = useState('list');

  const handleMenuOpen = (menu) => {
    setOpenMenu(menu);
    if (menu !== "date") setDateClicks(0);
    if (menu !== "type") setTypeClicks(0);
    if (menu !== "status") setStatusClicks(0);
    if (menu !== "platform") setPlatformClicks(0);
  };

  useEffect(() => {
    getPayments();
  }, [page, searchTerm, dateFilter, paymentTypeFilter, paymentStatusFilter, platformFilter]);

  const getPayments = () => {
    setLoading(true);
    AxiosUrl.post(`/fetch-bkash-payments/${uid}`, {
      page,
      limit,
      search: searchTerm,
      date: dateFilter,
      paymentType: paymentTypeFilter,
      paymentStatus: paymentStatusFilter,
      platform: platformFilter,
    })
      .then((res) => {
        if (res.data.Error) {
          toast.error(res.data.Error);
          setLoading(false);
          return;
        }
        setPayments(res.data.payments || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalPayments(res.data.totalPayments || 0);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to fetch payments");
        setLoading(false);
      });
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
    setPage(1);
  };

  const handlePaymentTypeChange = (event) => {
    setPaymentTypeFilter(event.target.value);
    setPage(1);
  };

  const handlePaymentStatusChange = (event) => {
    setPaymentStatusFilter(event.target.value);
    setPage(1);
  };

  const handlePlatformFilterChange = (event) => {
    setPlatformFilter(event.target.value);
    setPage(1);
  };

  const getPlatformDisplayText = (value) => {
    if (value === "web") return "Web";
    if (value === "mobile") return "Mobile";
    return "All Platforms";
  };

  const getDateDisplayText = (value) => {
    switch (value) {
      case "today":
        return "Today";
      case "yesterday":
        return "Yesterday";
      case "week":
        return "This Week";
      case "month":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      default:
        return "All Time";
    }
  };

  const getTypeDisplayText = (value) => {
    switch (value) {
      case "package":
        return "Package";
      case "recharge":
        return "Recharge";
      default:
        return "All Types";
    }
  };

  const getStatusDisplayText = (value) => {
    switch (value) {
      case "completed":
        return "Completed";
      case "pending":
        return "Pending";
      default:
        return "All Status";
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedPayments(payments);
      setSelectAll(true);
    } else {
      setSelectedPayments([]);
      setSelectAll(false);
    }
  };

  const handleSelectPayment = (payment) => {
    const isSelected = selectedPayments.some(p => p._id === payment._id);
    if (isSelected) {
      setSelectedPayments(selectedPayments.filter(p => p._id !== payment._id));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedPayments, payment];
      setSelectedPayments(newSelected);
      if (newSelected.length === payments.length) {
        setSelectAll(true);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedPayments.length === 0) {
      toast.warning("Please select payments to delete");
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedPayments.length} selected payment(s)?`)) {
      try {
        const response = await AxiosUrl.post(`/bulk-delete-bkash-payments/${uid}`, {
          payments: selectedPayments,
          deleteAll: false
        });

        if (response.data.success) {
          toast.success(response.data.message);
          setSelectedPayments([]);
          setSelectAll(false);
          getPayments();
        } else {
          toast.error(response.data.error || "Failed to delete payments");
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Failed to delete payments");
      }
    }
  };

  const handleDeleteAll = async () => {
    if (window.confirm("Are you sure you want to delete ALL payments? This action cannot be undone!")) {
      try {
        const response = await AxiosUrl.post(`/bulk-delete-bkash-payments/${uid}`, {
          deleteAll: true,
          paymentType: paymentTypeFilter
        });

        if (response.data.success) {
          toast.success(response.data.message);
          setSelectedPayments([]);
          setSelectAll(false);
          getPayments();
        } else {
          toast.error(response.data.error || "Failed to delete all payments");
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("Failed to delete all payments");
      }
    }
  };

  return (
    <div className="payments-container">
      <ToastContainer />
      <div className="payments-main">
        <div className="payments-content-wrapper">
          <div className="payments-header">
            <h2>Bkash Payments</h2>
          </div>

          {selectedPayments.length > 0 && (
            <div className="payments-bulk-actions">
              <span className="selected-count">{selectedPayments.length} selected</span>
              <Button
                className="bulk-delete-button"
                onClick={handleDeleteSelected}
                variant="contained"
              >
                Delete Selected
              </Button>
              <Button
                className="delete-all-button"
                onClick={handleDeleteAll}
                variant="contained"
              >
                Delete All
              </Button>
            </div>
          )}

          <div className="payments-filter-section">
            <div>
              <label className="payments-filter-label" style={{ backgroundColor: "#e91e63" }}>
                Search
              </label>
              <TextField
                variant="outlined"
                size="small"
                placeholder="Search by name, mobile, or payment ID..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="payments-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <ClickAwayListener
              onClickAway={() => {
                if (openMenu === "date") {
                  if (dateClicks > 0) {
                    setOpenMenu(null);
                  } else {
                    setDateClicks(dateClicks + 1);
                  }
                }
              }}
            >
              <div>
                <label className="payments-filter-label" style={{ backgroundColor: "#388e3c" }}>
                  Date
                </label>
                <TextField
                  select
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  variant="outlined"
                  size="small"
                  className="payments-date-filter"
                  onClick={() => handleMenuOpen("date")}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    },
                    displayEmpty: true,
                    renderValue: () => getDateDisplayText(dateFilter),
                  }}
                >
                  <MenuItem value="">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="week">This Week</MenuItem>
                  <MenuItem value="month">This Month</MenuItem>
                  <MenuItem value="lastMonth">Last Month</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>

            <ClickAwayListener
              onClickAway={() => {
                if (openMenu === "type") {
                  if (typeClicks > 0) {
                    setOpenMenu(null);
                  } else {
                    setTypeClicks(typeClicks + 1);
                  }
                }
              }}
            >
              <div>
                <label className="payments-filter-label" style={{ backgroundColor: "#3f51b5" }}>
                  Type
                </label>
                <TextField
                  select
                  value={paymentTypeFilter}
                  onChange={handlePaymentTypeChange}
                  variant="outlined"
                  size="small"
                  className="payments-type-filter"
                  onClick={() => handleMenuOpen("type")}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    },
                    displayEmpty: true,
                    renderValue: () => getTypeDisplayText(paymentTypeFilter),
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="package">Package</MenuItem>
                  <MenuItem value="recharge">Recharge</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>

            <ClickAwayListener
              onClickAway={() => {
                if (openMenu === "status") {
                  if (statusClicks > 0) {
                    setOpenMenu(null);
                  } else {
                    setStatusClicks(statusClicks + 1);
                  }
                }
              }}
            >
              <div>
                <label className="payments-filter-label" style={{ backgroundColor: "#f57c00" }}>
                  Status
                </label>
                <TextField
                  select
                  value={paymentStatusFilter}
                  onChange={handlePaymentStatusChange}
                  variant="outlined"
                  size="small"
                  className="payments-status-filter"
                  onClick={() => handleMenuOpen("status")}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    },
                    displayEmpty: true,
                    renderValue: () => getStatusDisplayText(paymentStatusFilter),
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>

            <ClickAwayListener
              onClickAway={() => {
                if (openMenu === "platform") {
                  if (platformClicks > 0) {
                    setOpenMenu(null);
                  } else {
                    setPlatformClicks(platformClicks + 1);
                  }
                }
              }}
            >
              <div>
                <label className="payments-filter-label" style={{ backgroundColor: "#2e7d32" }}>
                  Platform
                </label>
                <TextField
                  select
                  value={platformFilter}
                  onChange={handlePlatformFilterChange}
                  variant="outlined"
                  size="small"
                  className="payments-platform-filter"
                  onClick={() => handleMenuOpen("platform")}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        style: { maxHeight: 300 },
                      },
                    },
                    displayEmpty: true,
                    renderValue: () => getPlatformDisplayText(platformFilter),
                  }}
                >
                  <MenuItem value="">All Platforms</MenuItem>
                  <MenuItem value="web">Web</MenuItem>
                  <MenuItem value="mobile">Mobile</MenuItem>
                </TextField>
              </div>
            </ClickAwayListener>
          </div>

          {/* View Toggle Buttons */}
          <div className="payments-view-toggle">
            <IconButton
              onClick={() => setViewMode('grid')}
              color={viewMode === 'grid' ? 'primary' : 'default'}
              title="Grid View"
              style={{
                backgroundColor: viewMode === 'grid' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'grid' ? 'white' : '#666',
                marginRight: '8px'
              }}
            >
              <ViewModuleIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
              title="List View"
              style={{
                backgroundColor: viewMode === 'list' ? '#1976d2' : '#f5f5f5',
                color: viewMode === 'list' ? 'white' : '#666'
              }}
            >
              <ViewListIcon />
            </IconButton>
          </div>

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="payments-grid-container">
              {loading && <div className="google-progress-bar"></div>}
              {payments.length <= 0 && !loading ? (
                <div className="payments-no-data-available">
                  No payments available
                </div>
              ) : (
                <div className="payments-grid">
                  {payments.map((payment) => (
                    <Card key={payment._id} className="payments-grid-card">
                      <CardContent className="payments-grid-card-content">
                        <div className="payments-grid-header">
                          <div className="payments-grid-avatar-container">
                            {payment.user?.photo ? (
                              <Avatar 
                                alt={payment.user?.username || "User"} 
                                src={payment.user?.photo} 
                                className="payments-grid-avatar"
                              />
                            ) : (
                              <Avatar className="payments-grid-avatar">
                                <AccountCircleIcon />
                              </Avatar>
                            )}
                          </div>
                          <div className="payments-grid-header-info">
                            <Typography variant="h6" className="payments-grid-username">
                              {payment.user?.username || "Unknown"}
                            </Typography>
                            {payment.user?.mobile && (
                              <Typography variant="caption" className="payments-grid-mobile">
                                {payment.user.mobile}
                              </Typography>
                            )}
                          </div>
                        </div>
                        
                        <div className="payments-grid-details">
                          <div className="payments-grid-detail-item">
                            <Typography variant="caption" className="payments-grid-label">Type:</Typography>
                            <Chip
                              label={payment.paymentType}
                              size="small"
                              className={`payments-grid-type-chip ${
                                payment.paymentType === "Package" ? "type-package" : "type-recharge"
                              }`}
                              style={{
                                backgroundColor: payment.paymentType === "Package" ? "#9c27b0" : "#2196f3",
                                color: "white",
                                fontSize: "11px",
                                height: "24px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                              }}
                            />
                          </div>
                          
                          <div className="payments-grid-detail-item">
                            <Typography variant="caption" className="payments-grid-label">Amount:</Typography>
                            <Chip
                              label={`৳${payment.Amount || 0}`}
                              size="small"
                              style={{
                                backgroundColor: "#4caf50",
                                color: "white",
                                fontSize: "12px",
                                height: "26px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(76, 175, 80, 0.3)"
                              }}
                            />
                          </div>
                          
                          {!isMobile && (
                            <>
                              <div className="payments-grid-detail-item">
                                <Typography variant="caption" className="payments-grid-label">Payment ID:</Typography>
                                <Typography variant="body2" className="payments-grid-value payments-grid-id">
                                  {payment.BkashPaymentID || "N/A"}
                                </Typography>
                              </div>
                              
                              <div className="payments-grid-detail-item">
                                <Typography variant="caption" className="payments-grid-label">Date:</Typography>
                                <Typography variant="body2" className="payments-grid-value">
                                  {moment(payment.Date).utcOffset('+06:00').format("DD MMM YYYY")}
                                </Typography>
                              </div>
                              
                              <div className="payments-grid-detail-item">
                                <Typography variant="caption" className="payments-grid-label">Time:</Typography>
                                <Typography variant="body2" className="payments-grid-value">
                                  {moment(payment.Date).utcOffset('+06:00').format("hh:mm A")}
                                </Typography>
                              </div>
                            </>
                          )}
                          
                          <div className="payments-grid-detail-item">
                            <Typography variant="caption" className="payments-grid-label">Status:</Typography>
                            <Chip
                              label={payment.PaymentStatus ? "Completed" : "Pending"}
                              size="small"
                              icon={payment.PaymentStatus ? <CheckCircleIcon style={{ color: 'white' }} /> : <PendingIcon style={{ color: 'white' }} />}
                              style={{
                                backgroundColor: payment.PaymentStatus ? "#4caf50" : "#ff9800",
                                color: "white",
                                fontSize: "11px",
                                height: "26px",
                                fontWeight: "600",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                              }}
                            />
                          </div>
                        </div>
                      </CardContent>
                      <CardActions className="payments-grid-actions">
                        <Button
                          className="payment-delete-button"
                          onClick={() => {
                            DeletePaymentModal({
                              payment,
                              onSuccess: () => getPayments()
                            })();
                          }}
                          size="small"
                          startIcon={<DeleteIcon />}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* List/Table View */}
          {viewMode === 'list' && (
          <TableContainer component={Paper} className="payments-table-container">
            {loading && <div className="google-progress-bar"></div>}
            <Table className="payments-table" aria-label="payments table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={selectedPayments.length > 0 && selectedPayments.length < payments.length}
                    />
                  </TableCell>
                  <TableCell align="left">User</TableCell>
                  {!isSmallMobile && <TableCell align="left">Mobile</TableCell>}
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="center">Amount</TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="left">Payment ID</TableCell>
                      <TableCell align="center">Date</TableCell>
                    </>
                  )}
                  <TableCell align="center">Status</TableCell>
                  {!isMobile && <TableCell align="center">Platform</TableCell>}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length <= 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? (isSmallMobile ? 6 : 7) : 10} align="center">
                      <div className="payments-no-data-available">No payments available</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment._id} className="payments-table-row">
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPayments.some(p => p._id === payment._id)}
                          onChange={() => handleSelectPayment(payment)}
                        />
                      </TableCell>
                      <TableCell align="left">
                        <div className="payment-user-info">
                          <span className="payment-username">{payment.user.username}</span>
                        </div>
                      </TableCell>
                      {!isSmallMobile && (
                        <TableCell align="left">
                          <span className="payment-mobile">{payment.user.mobile || "N/A"}</span>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Button
                          className={`payment-type-button ${
                            payment.paymentType === "Package" ? "type-package" : "type-recharge"
                          }`}
                        >
                          {payment.paymentType}
                        </Button>
                      </TableCell>
                      <TableCell align="center">
                        <Button className="payment-amount-button">৳{payment.Amount || 0}</Button>
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell align="left">
                            <span className="payment-id">{payment.BkashPaymentID}</span>
                          </TableCell>
                          <TableCell align="center">
                            <span className="payment-date">
                              {moment(payment.Date).utcOffset('+06:00').format("DD MMM YYYY, hh:mm A")}
                            </span>
                          </TableCell>
                        </>
                      )}
                      <TableCell align="center">
                        <Button
                          className={`payment-status-button ${
                            payment.PaymentStatus ? "status-completed" : "status-pending"
                          }`}
                          startIcon={
                            payment.PaymentStatus ? (
                              <CheckCircleIcon fontSize="small" />
                            ) : (
                              <PendingIcon fontSize="small" />
                            )
                          }
                        >
                          {payment.PaymentStatus ? "Completed" : "Pending"}
                        </Button>
                      </TableCell>
                      {!isMobile && (
                        <TableCell align="center">
                          {(() => {
                            // payment.user.platform comes from the joined
                            // User doc (Signup.jsx stamps "web"). No platform
                            // field on the payment document itself — mobile
                            // payment writes are intentionally untouched.
                            const isWeb = payment.user?.platform === "web";
                            const known = payment.user?.platform === "web" || payment.user?.platform === "mobile";
                            return (
                              <Button
                                className="payment-platform-button"
                                style={{
                                  backgroundColor: isWeb
                                    ? "#2e7d32"
                                    : known
                                    ? "#455a64"
                                    : "#9e9e9e",
                                  color: "white",
                                  fontWeight: "500",
                                  fontSize: "12px",
                                  padding: "4px 12px",
                                  textTransform: "capitalize",
                                  minWidth: "72px",
                                }}
                              >
                                {isWeb ? "Web" : known ? "Mobile" : "Unknown"}
                              </Button>
                            );
                          })()}
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Button
                          className="payment-delete-button"
                          onClick={() => {
                            DeletePaymentModal({
                              payment,
                              onSuccess: () => getPayments()
                            })();
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          
          {/* Pagination - shown for both views */}
          <div className="payments-pagination-container">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              size="medium"
              siblingCount={1}
              boundaryCount={1}
              shape="rounded"
              className="payments-pagination"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Payments;
